import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import {
  CALENDAR_FIELD_STYLES,
  CALENDAR_ICON_BUTTON,
  CALENDAR_OUTLINE_BUTTON,
} from './controlStyles';
import type { CalendarViewMode } from './dateUtils';
import { parseISODate, toISODate } from './dateUtils';

export interface CalendarHeaderProps {
  anchor: Date;
  disabled?: boolean;
  onAnchorChange: (anchor: Date) => void;
  onViewModeChange: (mode: CalendarViewMode) => void;
  /** Disable one arrow, e.g. when there is nothing to step to. */
  previousDisabled?: boolean;
  nextDisabled?: boolean;
  today?: Date;
  viewMode: CalendarViewMode;
}

export const CalendarHeader = ({
  anchor,
  disabled = false,
  onAnchorChange,
  onViewModeChange,
  previousDisabled = false,
  nextDisabled = false,
  today,
  viewMode,
}: CalendarHeaderProps) => {
  const step = (direction: 1 | -1) => {
    if (viewMode === 'month') {
      onAnchorChange(new Date(anchor.getFullYear(), anchor.getMonth() + direction, 1));
      return;
    }

    onAnchorChange(
      new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate() + direction * 7)
    );
  };

  const title = anchor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex w-full items-center gap-2 sm:w-auto">
        <button
          aria-label="Previous"
          className={CALENDAR_ICON_BUTTON}
          disabled={disabled || previousDisabled}
          onClick={() => step(-1)}
          type="button"
        >
          <ChevronLeftIcon className="size-5" />
        </button>
        <button
          aria-label="Next"
          className={CALENDAR_ICON_BUTTON}
          disabled={disabled || nextDisabled}
          onClick={() => step(1)}
          type="button"
        >
          <ChevronRightIcon className="size-5" />
        </button>
        <h2 className="sub-heading-sb text-grey-100">{title}</h2>
      </div>

      <div className="grid w-full min-w-0 grid-cols-[minmax(0,1fr)_auto] gap-2 sm:flex sm:w-auto sm:items-center">
        <input
          aria-label="Jump to date"
          className={`${CALENDAR_FIELD_STYLES} min-w-0 w-full sm:w-auto cursor-pointer disabled:cursor-not-allowed`}
          disabled={disabled}
          onChange={(event) =>
            event.target.value && onAnchorChange(parseISODate(event.target.value))
          }
          type="date"
          value={toISODate(anchor)}
        />
        <button
          className={CALENDAR_OUTLINE_BUTTON}
          disabled={disabled}
          onClick={() => onAnchorChange(today ? new Date(today) : new Date())}
          type="button"
        >
          Today
        </button>
        <div
          className="col-span-2 flex overflow-hidden rounded-md border border-grey-800"
          role="group"
        >
          {(['month', 'week'] as CalendarViewMode[]).map((mode) => (
            <button
              aria-pressed={viewMode === mode}
              className={classNames(
                'body-text-sb flex-1 cursor-pointer px-3.5 py-2 capitalize transition-colors',
                'focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-inset',
                'disabled:cursor-not-allowed disabled:opacity-50',
                viewMode === mode
                  ? 'bg-primary-50 text-white focus-visible:ring-white/50'
                  : 'bg-white text-grey-500 hover:bg-grey-900 hover:text-grey-100 focus-visible:ring-primary-50/12'
              )}
              disabled={disabled}
              key={mode}
              onClick={() => onViewModeChange(mode)}
              type="button"
            >
              {mode}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
